<template>
    <CRow>
        <CCol>
            <div v-if="loading" class="text-center py-5">
                <CSpinner color="primary" />
                <p class="mt-2 text-muted">Загрузка заказа...</p>
            </div>

            <div v-else-if="order">
                <!-- Шапка -->
                <CCard>
                    <CCardHeader>
                        <div class="d-flex justify-content-between align-items-center">
                            <div class="d-flex align-items-center">
                                <CButton
                                    color="secondary"
                                    variant="outline"
                                    size="sm"
                                    class="mr-3"
                                    @click="$router.push('/iiko-orders')"
                                >
                                    <CIcon name="cil-arrow-left" />
                                </CButton>
                                <div>
                                    <strong class="mr-2">
                                        Заказ {{ order.number ? '#' + order.number : '' }}
                                        <span v-if="order.externalNumber" class="text-muted font-weight-normal ml-1 small">
                                            ({{ order.externalNumber }})
                                        </span>
                                    </strong>
                                    <CBadge :color="statusColor(order.status)">
                                        {{ statusLabel(order.status) }}
                                    </CBadge>
                                </div>
                            </div>
                            <CBadge color="info" class="px-3 py-2">{{ order._partner_name }}</CBadge>
                        </div>
                    </CCardHeader>
                </CCard>

                <CRow>
                    <!-- Основная информация -->
                    <CCol md="6">
                        <CCard>
                            <CCardHeader><strong>Информация о заказе</strong></CCardHeader>
                            <CCardBody class="p-0">
                                <table class="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr>
                                            <td class="font-weight-bold pl-3" style="width:180px">ID iiko:</td>
                                            <td class="pr-3"><code class="small">{{ order.id }}</code></td>
                                        </tr>
                                        <tr v-if="order.posId">
                                            <td class="font-weight-bold pl-3">POS ID:</td>
                                            <td class="pr-3"><code class="small">{{ order.posId }}</code></td>
                                        </tr>
                                        <tr>
                                            <td class="font-weight-bold pl-3">Статус:</td>
                                            <td class="pr-3">
                                                <CBadge :color="statusColor(order.status)">{{ statusLabel(order.status) }}</CBadge>
                                            </td>
                                        </tr>
                                        <tr v-if="order.orderType">
                                            <td class="font-weight-bold pl-3">Тип заказа:</td>
                                            <td class="pr-3">{{ order.orderType.name }}</td>
                                        </tr>
                                        <tr v-if="order.sourceKey">
                                            <td class="font-weight-bold pl-3">Источник:</td>
                                            <td class="pr-3"><CBadge color="secondary">{{ order.sourceKey }}</CBadge></td>
                                        </tr>
                                        <tr v-if="order.whenCreated">
                                            <td class="font-weight-bold pl-3">Создан:</td>
                                            <td class="pr-3">{{ formatDate(order.whenCreated) }}</td>
                                        </tr>
                                        <tr v-if="order.whenConfirmed">
                                            <td class="font-weight-bold pl-3">Подтверждён:</td>
                                            <td class="pr-3">{{ formatDate(order.whenConfirmed) }}</td>
                                        </tr>
                                        <tr v-if="order.cookingStartTime">
                                            <td class="font-weight-bold pl-3">Начало готовки:</td>
                                            <td class="pr-3">{{ formatDate(order.cookingStartTime) }}</td>
                                        </tr>
                                        <tr v-if="order.whenCookingCompleted">
                                            <td class="font-weight-bold pl-3">Готово:</td>
                                            <td class="pr-3">{{ formatDate(order.whenCookingCompleted) }}</td>
                                        </tr>
                                        <tr v-if="order.whenPacked">
                                            <td class="font-weight-bold pl-3">Упаковано:</td>
                                            <td class="pr-3">{{ formatDate(order.whenPacked) }}</td>
                                        </tr>
                                        <tr v-if="order.whenSended">
                                            <td class="font-weight-bold pl-3">Отправлен:</td>
                                            <td class="pr-3">{{ formatDate(order.whenSended) }}</td>
                                        </tr>
                                        <tr v-if="order.whenDelivered">
                                            <td class="font-weight-bold pl-3">Доставлен:</td>
                                            <td class="pr-3">{{ formatDate(order.whenDelivered) }}</td>
                                        </tr>
                                        <tr v-if="order.whenClosed">
                                            <td class="font-weight-bold pl-3">Закрыт:</td>
                                            <td class="pr-3">{{ formatDate(order.whenClosed) }}</td>
                                        </tr>
                                        <tr v-if="order.completeBefore">
                                            <td class="font-weight-bold pl-3">Выдать до:</td>
                                            <td class="pr-3">{{ formatDate(order.completeBefore) }}</td>
                                        </tr>
                                        <tr v-if="order.isAsap !== undefined">
                                            <td class="font-weight-bold pl-3">ASAP:</td>
                                            <td class="pr-3">
                                                <CBadge :color="order.isAsap ? 'warning' : 'secondary'">
                                                    {{ order.isAsap ? 'Да' : 'Нет' }}
                                                </CBadge>
                                            </td>
                                        </tr>
                                        <tr class="table-light">
                                            <td class="font-weight-bold pl-3">Сумма:</td>
                                            <td class="pr-3 font-weight-bold">
                                                {{ order.sum != null ? order.sum + ' ₽' : '—' }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </CCardBody>
                        </CCard>

                        <!-- Комментарий -->
                        <CCard v-if="order.comment">
                            <CCardHeader><strong>Комментарий к заказу</strong></CCardHeader>
                            <CCardBody>
                                <p class="mb-0" style="white-space: pre-wrap">{{ order.comment }}</p>
                            </CCardBody>
                        </CCard>

                        <!-- Проблема -->
                        <CCard v-if="order.problem" class="border-danger">
                            <CCardHeader class="bg-danger text-white"><strong>Проблема</strong></CCardHeader>
                            <CCardBody>{{ order.problem }}</CCardBody>
                        </CCard>
                    </CCol>

                    <!-- Правая колонка -->
                    <CCol md="6">
                        <!-- Клиент -->
                        <CCard>
                            <CCardHeader><strong>Клиент</strong></CCardHeader>
                            <CCardBody class="p-0">
                                <table class="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr v-if="order.customer && order.customer.name">
                                            <td class="font-weight-bold pl-3" style="width:140px">Имя:</td>
                                            <td class="pr-3">{{ order.customer.name }}</td>
                                        </tr>
                                        <tr v-if="order.phone">
                                            <td class="font-weight-bold pl-3">Телефон:</td>
                                            <td class="pr-3">
                                                <a :href="'tel:' + order.phone">{{ order.phone }}</a>
                                            </td>
                                        </tr>
                                        <tr v-if="order.customer && order.customer.comment">
                                            <td class="font-weight-bold pl-3">Заметка:</td>
                                            <td class="pr-3">{{ order.customer.comment }}</td>
                                        </tr>
                                        <tr v-if="order.customer && order.customer.inBlacklist">
                                            <td class="font-weight-bold pl-3">Чёрный список:</td>
                                            <td class="pr-3">
                                                <CBadge color="danger">Да</CBadge>
                                                <span v-if="order.customer.blacklistReason" class="ml-1 small">{{ order.customer.blacklistReason }}</span>
                                            </td>
                                        </tr>
                                        <tr v-if="order.guestsInfo">
                                            <td class="font-weight-bold pl-3">Гостей:</td>
                                            <td class="pr-3">{{ order.guestsInfo.count }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </CCardBody>
                        </CCard>

                        <!-- Адрес доставки -->
                        <CCard v-if="order.deliveryPoint">
                            <CCardHeader><strong>Адрес доставки</strong></CCardHeader>
                            <CCardBody class="p-0">
                                <table class="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr v-if="order.deliveryPoint.address">
                                            <td class="pl-3 pr-3" colspan="2">
                                                {{ formatAddress(order.deliveryPoint.address) }}
                                            </td>
                                        </tr>
                                        <tr v-if="order.deliveryPoint.comment">
                                            <td class="font-weight-bold pl-3" style="width:140px">Комментарий:</td>
                                            <td class="pr-3">{{ order.deliveryPoint.comment }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </CCardBody>
                        </CCard>

                        <!-- Оператор -->
                        <CCard v-if="order.operator">
                            <CCardHeader><strong>Оператор</strong></CCardHeader>
                            <CCardBody class="p-0">
                                <table class="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr>
                                            <td class="font-weight-bold pl-3" style="width:140px">Имя:</td>
                                            <td class="pr-3">{{ order.operator.name }}</td>
                                        </tr>
                                        <tr v-if="order.operator.phone">
                                            <td class="font-weight-bold pl-3">Телефон:</td>
                                            <td class="pr-3">{{ order.operator.phone }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </CCardBody>
                        </CCard>

                        <!-- Курьер -->
                        <CCard v-if="order.courierInfo">
                            <CCardHeader><strong>Курьер</strong></CCardHeader>
                            <CCardBody class="p-0">
                                <table class="table table-sm table-borderless mb-0">
                                    <tbody>
                                        <tr v-if="order.courierInfo.courier">
                                            <td class="font-weight-bold pl-3" style="width:140px">Имя:</td>
                                            <td class="pr-3">{{ order.courierInfo.courier.name }}</td>
                                        </tr>
                                        <tr v-if="order.courierInfo.courier && order.courierInfo.courier.phone">
                                            <td class="font-weight-bold pl-3">Телефон:</td>
                                            <td class="pr-3">{{ order.courierInfo.courier.phone }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </CCardBody>
                        </CCard>
                    </CCol>
                </CRow>

                <!-- Состав заказа -->
                <CCard v-if="order.items && order.items.length">
                    <CCardHeader>
                        <strong>Состав заказа</strong>
                        <span class="ml-2 text-muted">{{ order.items.length }} {{ declension(order.items.length, ['позиция', 'позиции', 'позиций']) }}</span>
                    </CCardHeader>
                    <CCardBody class="p-0">
                        <table class="table table-hover mb-0">
                            <thead class="thead-light">
                                <tr>
                                    <th class="pl-3">Блюдо</th>
                                    <th class="text-center" style="width:80px">Кол-во</th>
                                    <th class="text-right" style="width:110px">Цена</th>
                                    <th class="text-right pr-3" style="width:110px">Итого</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in order.items" :key="item.positionId">
                                    <td class="pl-3">
                                        <div class="font-weight-bold">
                                            {{ item.product ? item.product.name : '—' }}
                                        </div>
                                        <!-- Модификаторы -->
                                        <div v-if="item.modifiers && item.modifiers.length" class="mt-1">
                                            <span
                                                v-for="mod in item.modifiers"
                                                :key="mod.positionId"
                                                class="badge badge-light border mr-1 small"
                                            >
                                                {{ mod.product ? mod.product.name : '—' }}
                                                <span v-if="mod.price > 0" class="text-success ml-1">+{{ mod.price }} ₽</span>
                                            </span>
                                        </div>
                                        <div v-if="item.comment" class="text-muted small mt-1">{{ item.comment }}</div>
                                        <!-- Статус позиции -->
                                        <CBadge v-if="item.status" :color="itemStatusColor(item.status)" class="mt-1 small">
                                            {{ itemStatusLabel(item.status) }}
                                        </CBadge>
                                    </td>
                                    <td class="text-center align-middle">{{ item.amount }}</td>
                                    <td class="text-right align-middle">{{ item.price != null ? item.price + ' ₽' : '—' }}</td>
                                    <td class="text-right pr-3 align-middle font-weight-bold">
                                        {{ item.resultSum != null ? item.resultSum + ' ₽' : (item.cost != null ? item.cost + ' ₽' : '—') }}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="table-secondary">
                                <tr>
                                    <td colspan="3" class="text-right font-weight-bold pl-3">Итого:</td>
                                    <td class="text-right pr-3 font-weight-bold">{{ order.sum != null ? order.sum + ' ₽' : '—' }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </CCardBody>
                </CCard>

                <!-- Оплата -->
                <CCard v-if="order.payments && order.payments.length">
                    <CCardHeader><strong>Оплата</strong></CCardHeader>
                    <CCardBody class="p-0">
                        <table class="table table-sm mb-0">
                            <thead class="thead-light">
                                <tr>
                                    <th class="pl-3">Тип оплаты</th>
                                    <th>Вид</th>
                                    <th class="text-right pr-3">Сумма</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(p, i) in order.payments" :key="i">
                                    <td class="pl-3">{{ p.paymentType ? p.paymentType.name : '—' }}</td>
                                    <td>
                                        <span class="text-muted small">{{ p.paymentType ? p.paymentType.kind : '' }}</span>
                                        <CBadge v-if="p.isPrepay" color="warning" class="ml-1">Предоплата</CBadge>
                                        <CBadge v-if="p.isExternal" color="info" class="ml-1">Внешняя</CBadge>
                                    </td>
                                    <td class="text-right pr-3 font-weight-bold">{{ p.sum }} ₽</td>
                                </tr>
                            </tbody>
                        </table>
                    </CCardBody>
                </CCard>

                <!-- JSON для отладки -->
                <CCard>
                    <CCardHeader>
                        <div class="d-flex justify-content-between align-items-center">
                            <strong class="text-muted">Полные данные (JSON)</strong>
                            <CButton color="secondary" variant="outline" size="sm" @click="showRaw = !showRaw">
                                {{ showRaw ? 'Скрыть' : 'Показать' }}
                            </CButton>
                        </div>
                    </CCardHeader>
                    <CCardBody v-if="showRaw" class="p-0">
                        <pre class="small p-3 mb-0" style="max-height:400px;overflow:auto;background:#f8f9fa">{{ JSON.stringify(order, null, 2) }}</pre>
                    </CCardBody>
                </CCard>
            </div>

            <div v-else-if="!loading" class="text-center text-muted py-5">
                Заказ не найден
            </div>
        </CCol>
    </CRow>
</template>

<script>
import moment from 'moment'

const IIKO_STATUSES = {
    WaitCooking: 'Ожидает готовки',
    ReadyForCooking: 'Готово к готовке',
    CookingStarted: 'Готовится',
    CookingCompleted: 'Приготовлен',
    Waiting: 'Ожидает выдачи',
    OnWay: 'В пути',
    Delivered: 'Доставлен',
    Closed: 'Закрыт',
    Cancelled: 'Отменён',
    Unconfirmed: 'Не подтверждён',
    New: 'Новый',
    Served: 'Выдан',
}

const STATUS_COLORS = {
    WaitCooking: 'warning',
    ReadyForCooking: 'warning',
    CookingStarted: 'primary',
    CookingCompleted: 'info',
    Waiting: 'secondary',
    OnWay: 'primary',
    Delivered: 'success',
    Closed: 'dark',
    Cancelled: 'danger',
    Unconfirmed: 'secondary',
    New: 'info',
    Served: 'success',
}

const ITEM_STATUSES = {
    WaitCooking: 'Ожидает',
    CookingStarted: 'Готовится',
    CookingCompleted: 'Готово',
    Served: 'Выдано',
    Cancelled: 'Отменено',
}

export default {
    name: 'IikoOrderDetail',
    data() {
        return {
            loading: false,
            order: null,
            showRaw: false
        }
    },
    async mounted() {
        await this.fetchOrder()
    },
    methods: {
        async fetchOrder() {
            const { partnerAlias, orderId } = this.$route.params
            this.loading = true
            try {
                const { data } = await this.$http.get(`/iiko-orders/${partnerAlias}/${orderId}`)
                this.order = data
            } catch (e) {
                this.$root.$emit('showError', 'Ошибка загрузки заказа')
                console.error(e)
            }
            this.loading = false
        },
        statusLabel(status) {
            return IIKO_STATUSES[status] || status || '—'
        },
        statusColor(status) {
            return STATUS_COLORS[status] || 'secondary'
        },
        itemStatusLabel(status) {
            return ITEM_STATUSES[status] || status || ''
        },
        itemStatusColor(status) {
            return STATUS_COLORS[status] || 'secondary'
        },
        formatDate(date) {
            if (!date) return '—'
            return moment(date).format('DD.MM.YYYY HH:mm:ss')
        },
        formatAddress(address) {
            if (!address) return '—'
            const parts = []
            if (address.city && address.city.name) parts.push(address.city.name)
            if (address.street && address.street.name) parts.push(address.street.name)
            if (address.house) parts.push('д. ' + address.house)
            if (address.flat) parts.push('кв. ' + address.flat)
            if (address.entrance) parts.push('подъезд ' + address.entrance)
            if (address.floor) parts.push('этаж ' + address.floor)
            return parts.join(', ') || '—'
        },
        declension(n, forms) {
            const abs = Math.abs(n) % 100
            const rem = abs % 10
            if (abs > 10 && abs < 20) return forms[2]
            if (rem > 1 && rem < 5) return forms[1]
            if (rem === 1) return forms[0]
            return forms[2]
        }
    }
}
</script>
